import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { handleEmailAction } from '../../utils/emailActions';
import { PasswordResetForm } from './PasswordResetForm';
import type { EmailActionState } from '../../types/auth';

export const EmailActionHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<EmailActionState>({
    status: 'loading',
    mode: null,
    message: 'Verarbeite Anfrage...'
  });

  useEffect(() => {
    const processEmailAction = async () => {
      try {
        // Extract action code and mode from URL
        const actionCode = searchParams.get('oobCode');
        const mode = searchParams.get('mode');
        const continueUrl = searchParams.get('continueUrl');

        if (!actionCode || !mode) {
          setState({
            status: 'error',
            mode,
            message: 'Ungültiger oder fehlender Aktionscode'
          });
          return;
        }

        // Process the email action
        const result = await handleEmailAction(actionCode, mode);
        setState(result);

        // Redirect after successful action
        if (result.status === 'success') {
          setTimeout(() => {
            if (continueUrl) {
              window.location.href = continueUrl;
            } else {
              navigate('/');
            }
          }, 3000);
        }
      } catch (error: any) {
        setState({
          status: 'error',
          mode: searchParams.get('mode'),
          message: error.message || 'Ein Fehler ist aufgetreten'
        });
      }
    };

    processEmailAction();
  }, [searchParams, navigate]);

  const renderIcon = () => {
    switch (state.status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 text-indigo-600 dark:text-indigo-400 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (state.status === 'loading') return 'Wird verarbeitet...';
    
    if (state.status === 'resetPassword') {
      return 'Neues Passwort festlegen';
    }
    
    switch (state.mode) {
      case 'resetPassword':
        return state.status === 'success' 
          ? 'Passwort zurückgesetzt' 
          : 'Passwort zurücksetzen';
      case 'verifyEmail':
        return state.status === 'success'
          ? 'E-Mail verifiziert'
          : 'E-Mail verifizieren';
      case 'recoverEmail':
        return state.status === 'success'
          ? 'E-Mail wiederhergestellt'
          : 'E-Mail wiederherstellen';
      default:
        return 'E-Mail-Aktion';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-100 via-white to-purple-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center pt-6 sm:pt-8">
            <div className="flex items-center space-x-2">
              <Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-600 dark:text-indigo-400" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Equisuite Keultjes
              </h1>
            </div>
          </div>

          <div className="mt-8 sm:mt-12 md:mt-16 mb-12 sm:mb-16">
            <div className="bg-white dark:bg-gray-800 w-full max-w-md mx-auto rounded-lg shadow-xl overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col items-center text-center space-y-4">
                  {renderIcon()}
                  
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {getTitle()}
                  </h2>
                  
                  {state.status === 'resetPassword' && searchParams.get('oobCode') ? (
                    <PasswordResetForm actionCode={searchParams.get('oobCode')!} />
                  ) : (
                    <div className="text-center">
                      {state.status !== 'loading' && (
                        <p className={`text-sm ${
                          state.status === 'error' 
                            ? 'text-red-600 dark:text-red-400' 
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {state.message}
                        </p>
                      )}

                      {state.status === 'success' && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Sie werden in Kürze weitergeleitet...
                        </p>
                      )}

                      {state.status === 'error' && (
                        <button
                          onClick={() => navigate('/')}
                          className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Zur Anmeldung
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
